import React, { useState } from "react";
import { UserCircle, X, Upload } from "lucide-react";
import { Profile } from "../schedule/types/types";
import accessURL from "../../../components/utils/access";

interface ProfileProps {
  data: Profile;
}

const UpdateProfile: React.FC<ProfileProps> = ({ data }) => {
  const [open, setOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: data.name,
    email: data.email,
  });

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      // Create preview URL for the new file
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  // Check if form has been modified
  const isFormModified =
    formData.name.trim() !== data.name ||
    formData.email.trim() !== data.email ||
    logoFile !== null;

  // Check if required fields are empty
  const isFormValid =
    formData.name.trim() !== "" && formData.email.trim() !== "";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Create FormData to handle file upload
    const submitData = new FormData();
    submitData.append("name", formData.name.trim());
    submitData.append("email", formData.email.trim());
    if (logoFile) {
      submitData.append("logo", logoFile);
    }

    try {
      const res = await accessURL.post("profile", submitData);
      if (res.status === 200) {
        // Clear form
        setFormData({
          name: "",
          email: "",
        });
        setLogoFile(null);
        setPreviewUrl(null);
        setOpen(false);

        // Redirect to home page
        window.location.href = "/";
      }
    } catch (error) {
      console.error("Error updating profile:", error);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="bg-black px-8 py-3 rounded-xl hover:bg-blue-700 text-white hover:text-white border-none flex items-center gap-2"
      >
        <UserCircle size={20} />
        Update Profile
      </button>

      {/* Modal Overlay */}
      {open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          {/* Modal Content */}
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 relative">
            {/* Close Button */}
            <button
              onClick={() => setOpen(false)}
              className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
            >
              <X size={20} />
            </button>

            <h2 className="text-2xl font-bold mb-6">Update Profile</h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  required
                />
              </div>

              {/* Email Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  required
                />
              </div>

              {/* Logo File Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Logo (Optional)
                </label>
                <div className="mt-1 flex items-center">
                  <label className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                    <Upload className="h-5 w-5 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-600">
                      {logoFile ? logoFile.name : "Choose file"}
                    </span>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                  </label>
                </div>
              </div>

              {/* New Logo Preview - only shown for newly uploaded files */}
              {previewUrl && (
                <div className="mt-2">
                  <p className="text-sm font-medium text-gray-700 mb-1">
                    New Logo Preview
                  </p>
                  <div className="w-full h-40 border border-gray-300 rounded-lg overflow-hidden flex items-center justify-center bg-gray-50">
                    <img
                      src={previewUrl}
                      alt="New logo preview"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!isFormModified || !isFormValid}
                className={`w-full py-3 rounded-lg text-white font-medium
                  ${
                    isFormModified && isFormValid
                      ? "bg-blue-600 hover:bg-blue-700"
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
              >
                Update
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default UpdateProfile;
